import Placeholder from "@tiptap/extension-placeholder";
import { Markdown } from "@tiptap/markdown";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useRef } from "react";

import type { UploadedWorkOrderFile } from "@/hooks/useWorkOrderFileUpload";
import { resolveWorkOrderFileSrc, revokeWorkOrderFilePreviewUrl, parseWorkOrderFileId } from "@/lib/workOrderFiles";
import { hostedVideoFromClipboard } from "@/lib/hostedVideo";
import { cn } from "@/lib/utils";

import { WorkOrderImage } from "./lib/workOrderDescriptionImage";
import { WorkOrderRequestImage } from "./lib/workOrderRequestImage";
import { insertHostedVideo, insertUploadedFiles } from "./lib/workOrderDescriptionFiles";
import { pasteMarkdownFromClipboard } from "./lib/workOrderDescriptionMarkdown";
import { WorkspaceUnderline } from "./lib/workspaceUnderline";
import { WorkOrderDescriptionFormatToolbar } from "./WorkOrderDescriptionFormatToolbar";
import { WorkOrderDescriptionSkillSlash } from "./WorkOrderDescriptionSkillSlash";

interface WorkOrderDescriptionEditorProps {
  value: string;
  maxLength: number;
  disabled: boolean;
  onChange: (next: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  placeholder?: string;
  fileUrls?: Record<string, string>;
  fileContentTypes?: Record<string, string>;
  onUploadFiles?: (files: FileList | File[]) => Promise<UploadedWorkOrderFile[]>;
  isUploading?: boolean;
  canRemoveImages?: boolean;
  autoFocus?: boolean;
  organizationId?: string;
  factoryId?: string;
}

function areUrlMapsEqual(a?: Record<string, string>, b?: Record<string, string>): boolean {
  if (a === b) {
    return true;
  }
  const keysA = Object.keys(a ?? {});
  const keysB = Object.keys(b ?? {});
  if (keysA.length === 0 && keysB.length === 0) {
    return true;
  }
  if (keysA.length !== keysB.length) {
    return false;
  }
  for (const key of keysA) {
    if ((a ?? {})[key] !== (b ?? {})[key]) {
      return false;
    }
  }
  return true;
}

export function WorkOrderDescriptionEditor({
  value,
  maxLength,
  disabled,
  onChange,
  onFocus,
  onBlur,
  className,
  placeholder = "Add description…",
  fileUrls,
  fileContentTypes,
  onUploadFiles,
  isUploading = false,
  canRemoveImages = false,
  autoFocus = false,
  organizationId,
  factoryId,
}: WorkOrderDescriptionEditorProps) {
  const editorRef = useRef<Editor | null>(null);
  const skillKeyDownRef = useRef<(event: KeyboardEvent) => boolean>(() => false);
  const onChangeRef = useRef(onChange);
  const onFocusRef = useRef(onFocus);
  const onBlurRef = useRef(onBlur);
  const maxLengthRef = useRef(maxLength);
  const emittedMarkdownRef = useRef(value);
  const pasteInFlightRef = useRef(false);
  const onUploadFilesRef = useRef(onUploadFiles);
  const fileUrlsRef = useRef(fileUrls);

  onChangeRef.current = onChange;
  onFocusRef.current = onFocus;
  onBlurRef.current = onBlur;
  maxLengthRef.current = maxLength;
  onUploadFilesRef.current = onUploadFiles;
  fileUrlsRef.current = fileUrls;

  const editor = useEditor({
    immediatelyRender: false,
    autofocus: autoFocus ? "end" : false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
        link: { openOnClick: false, autolink: true },
        underline: false,
      }),
      WorkspaceUnderline,
      canRemoveImages ? WorkOrderRequestImage : WorkOrderImage,
      Markdown,
      Placeholder.configure({ placeholder }),
    ],
    content: value,
    contentType: "markdown",
    editable: !disabled,
    editorProps: {
      attributes: {
        id: "work-order-description-input",
        class: cn(
          "work-order-description-editor workspace-markdown min-h-40 outline-none",
          className ?? "text-[15px] leading-6",
        ),
        "data-testid": "work-order-description-input",
      },
      handleKeyDown: (_view, event) => skillKeyDownRef.current(event),
      handlePaste: (_view, event) => {
        if (tryUploadClipboardFiles(editorRef.current, event, onUploadFilesRef.current, disabled)) {
          return true;
        }
        const text = event.clipboardData?.getData("text/plain") ?? "";
        const hosted = hostedVideoFromClipboard(text);
        const current = editorRef.current;
        if (hosted && current) {
          event.preventDefault();
          insertHostedVideo(current, hosted);
          return true;
        }
        if (!text) {
          return false;
        }
        pasteInFlightRef.current = true;
        queueMicrotask(() => {
          pasteInFlightRef.current = false;
        });
        if (!current) {
          pasteInFlightRef.current = false;
          return false;
        }
        return pasteMarkdownFromClipboard(current, event);
      },
      handleDrop: (_view, event) => {
        return tryUploadDataTransferFiles(
          editorRef.current,
          event.dataTransfer?.files,
          onUploadFilesRef.current,
          disabled,
        );
      },
    },
    onUpdate: ({ editor: current }) => {
      const markdown = current.getMarkdown();
      const limit = maxLengthRef.current;
      const previous = emittedMarkdownRef.current;
      const fromPaste = pasteInFlightRef.current;
      pasteInFlightRef.current = false;
      const next = markdownWithinLimit(markdown, previous, limit, fromPaste);
      if (next !== markdown) {
        current.commands.setContent(next, {
          contentType: "markdown",
          emitUpdate: false,
        });
      }
      if (next === previous) {
        return;
      }
      emittedMarkdownRef.current = next;
      onChangeRef.current(next);
    },
    onFocus: () => {
      onFocusRef.current?.();
    },
    onBlur: () => {
      onBlurRef.current?.();
    },
  });

  editorRef.current = editor;
  useWorkOrderEditorResolvedSrc(editor, fileUrls, fileContentTypes);

  useEffect(() => {
    if (!editor) {
      return;
    }
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!autoFocus || disabled || !editor) {
      return;
    }
    editor.commands.focus("end");
  }, [autoFocus, disabled, editor]);

  useEffect(() => {
    if (!editor) {
      return;
    }
    if (value === emittedMarkdownRef.current) {
      return;
    }
    emittedMarkdownRef.current = value;
    editor.commands.setContent(value, { contentType: "markdown" });
  }, [editor, value]);

  useEffect(() => {
    return () => {
      const markdown = emittedMarkdownRef.current;
      for (const match of markdown.matchAll(/sp-file:\/\/([0-9a-fA-F-]+)/g)) {
        const id = parseWorkOrderFileId(`sp-file://${match[1]}`);
        if (id) {
          revokeWorkOrderFilePreviewUrl(id);
        }
      }
    };
  }, [editor]);

  return (
    <>
      {editor ? (
        <BubbleMenu
          editor={editor}
          updateDelay={0}
          appendTo={() =>
            editor.view.dom.closest('[data-testid="create-work-order-dialog"]') ??
            editor.view.dom.closest('[data-testid="create-work-order-request-dialog"]') ??
            editor.view.dom.closest('[data-testid="work-order-split-run"]') ??
            editor.view.dom.parentElement ??
            document.body
          }
          options={{ placement: "top", offset: 8, strategy: "fixed" }}
        >
          <WorkOrderDescriptionFormatToolbar
            disabled={disabled || isUploading}
            editor={editor}
            onUploadFiles={onUploadFiles}
          />
        </BubbleMenu>
      ) : null}
      <div className="relative">
        {editor && factoryId ? (
          <WorkOrderDescriptionSkillSlash
            editor={editor}
            organizationId={organizationId}
            factoryId={factoryId}
            keyDownRef={skillKeyDownRef}
          />
        ) : null}
        <EditorContent editor={editor} />
      </div>
    </>
  );
}

function useWorkOrderEditorResolvedSrc(
  editor: Editor | null,
  fileUrls?: Record<string, string>,
  fileContentTypes?: Record<string, string>,
) {
  const lastFileUrlsRef = useRef<Record<string, string> | undefined>(undefined);
  const lastEditorRef = useRef<Editor | null>(null);

  useEffect(() => {
    if (!editor) {
      return;
    }
    const urls = fileUrls ?? {};
    editor.storage.image = {
      ...(editor.storage.image ?? {}),
      downloadUrls: urls,
      contentTypes: { ...(editor.storage.image?.contentTypes ?? {}), ...(fileContentTypes ?? {}) },
    };
    const isNewEditor = editor !== lastEditorRef.current;
    const urlsChanged = !areUrlMapsEqual(lastFileUrlsRef.current, fileUrls);
    if (!isNewEditor && !urlsChanged) {
      return;
    }
    lastEditorRef.current = editor;
    lastFileUrlsRef.current = fileUrls;
    if (Object.keys(urls).length === 0 && !urlsChanged) {
      return;
    }

    const { state, view } = editor;
    const tr = state.tr;
    state.doc.descendants((node, pos) => {
      if (node.type.name === "image") {
        const resolved = resolveWorkOrderFileSrc(node.attrs.src as string | undefined, urls);
        if (node.attrs.resolvedSrc !== resolved) {
          tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            resolvedSrc: resolved,
          });
        }
      }
    });
    if (tr.docChanged) {
      view.dispatch(tr);
    }
  }, [editor, fileUrls, fileContentTypes]);
}

function tryUploadClipboardFiles(
  editor: Editor | null,
  event: ClipboardEvent,
  onUploadFiles: WorkOrderDescriptionEditorProps["onUploadFiles"],
  disabled: boolean,
): boolean {
  return tryUploadDataTransferFiles(editor, event.clipboardData?.files, onUploadFiles, disabled);
}

function tryUploadDataTransferFiles(
  editor: Editor | null,
  files: FileList | undefined,
  onUploadFiles: WorkOrderDescriptionEditorProps["onUploadFiles"],
  disabled: boolean,
): boolean {
  if (disabled || !onUploadFiles || !editor || !files || files.length === 0) {
    return false;
  }
  void onUploadFiles(files).then((uploaded) => insertUploadedFiles(editor, uploaded));
  return true;
}

function markdownWithinLimit(markdown: string, previous: string, limit: number, fromPaste: boolean): string {
  if (markdown.length <= limit) {
    return markdown;
  }
  if (fromPaste) {
    return markdown.slice(0, limit);
  }
  return previous;
}
